import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';

/**
 * Color theme options for the progress bar
 */
export type ProgressBarColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'red'
  | 'green'
  | 'indigo'
  | 'gray';

/**
 * Size options for the progress bar height
 */
export type ProgressBarSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * ProgressBar - A simple progress bar component
 *
 * Displays a horizontal progress bar with customizable color and size.
 * Used for utilization, completion, and score indicators.
 *
 * @example Basic usage
 * ```gts
 * <ProgressBar @value={{75}} @color="emerald" />
 * ```
 *
 * @example With custom max and size
 * ```gts
 * <ProgressBar @value={{42}} @max={{100}} @color="violet" @size="lg" />
 * ```
 *
 * @example Gradient style
 * ```gts
 * <ProgressBar @value={{60}} @gradient={{true}} @color="blue" />
 * ```
 */
interface ProgressBarSignature {
  Args: {
    /** Current value (0 to max) */
    value: number;
    /** Maximum value (default: 100) */
    max?: number;
    /** Color theme */
    color?: ProgressBarColor;
    /** Size variant */
    size?: ProgressBarSize;
    /** Use gradient fill instead of solid */
    gradient?: boolean;
    /** Custom aria-label */
    label?: string;
  };
  Element: HTMLDivElement;
}

const colorClasses: Record<ProgressBarColor, { solid: string; gradient: string }> = {
  emerald: { solid: 'bg-emerald-500', gradient: 'bg-gradient-to-r from-emerald-400 to-teal-500' },
  blue: { solid: 'bg-blue-500', gradient: 'bg-gradient-to-r from-blue-400 to-indigo-500' },
  violet: { solid: 'bg-violet-500', gradient: 'bg-gradient-to-r from-violet-400 to-purple-500' },
  amber: { solid: 'bg-amber-500', gradient: 'bg-gradient-to-r from-amber-400 to-orange-500' },
  teal: { solid: 'bg-teal-500', gradient: 'bg-gradient-to-r from-teal-400 to-cyan-500' },
  red: { solid: 'bg-red-500', gradient: 'bg-gradient-to-r from-red-400 to-rose-500' },
  green: { solid: 'bg-green-500', gradient: 'bg-gradient-to-r from-green-400 to-emerald-500' },
  indigo: { solid: 'bg-indigo-500', gradient: 'bg-gradient-to-r from-indigo-400 to-violet-500' },
  gray: { solid: 'bg-gray-500', gradient: 'bg-gradient-to-r from-gray-400 to-slate-500' },
};

const sizeClasses: Record<ProgressBarSize, string> = {
  xs: 'h-1',
  sm: 'h-2',
  md: 'h-3',
  lg: 'h-4',
};

export default class ProgressBar extends Component<ProgressBarSignature> {
  get max() {
    return this.args.max ?? 100;
  }

  get percent() {
    const value = Math.max(0, Math.min(this.args.value, this.max));
    return Math.round((value / this.max) * 100);
  }

  get widthStyle() {
    return htmlSafe(`width: ${this.percent}%`);
  }

  get color() {
    return this.args.color ?? 'blue';
  }

  get fillClass() {
    const colors = colorClasses[this.color];
    return this.args.gradient ? colors.gradient : colors.solid;
  }

  get sizeClass() {
    return sizeClasses[this.args.size ?? 'sm'];
  }

  get ariaLabel() {
    return this.args.label ?? `${this.percent}% complete`;
  }

  <template>
    <div
      class="{{this.sizeClass}} bg-gray-200 rounded-full overflow-hidden"
      role="progressbar"
      aria-valuenow={{this.percent}}
      aria-valuemin={{0}}
      aria-valuemax={{100}}
      aria-label={{this.ariaLabel}}
      ...attributes
    >
      <div
        class="h-full {{this.fillClass}} rounded-full transition-all duration-300"
        style={{this.widthStyle}}
      ></div>
    </div>
  </template>
}
